import {BRAND_KEY} from '../../../brand'
import {remCalc} from '../../_utils'
import getSemantics from '../../semantics'
import {Responsive} from '../../types/semantics.types'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

const DS = {
    bgBaseDefault: '#efedea',
    bgBaseElevated: '#f4f3f1',
    bgBaseOver: '#f2f0ee',
    bgBaseUnder: '#e7e6e4',
    bgBaseOverlay: 'rgba(19,24,58,0.4)',
    bgAccentDefault: '#001787',
    bgAccentHover: '#374b8a',
    bgAccentMuted: '#8a95b7',
    bgNeutralMuted: '#dedcda',
    fgNeutralDefault: '#13183a',
    fgNeutralMuted: '#484956',
    fgNeutralSubtle: '#5d5f69',
    fgNeutralDim: '#b2b1b4',
    fgNeutralInverse: '#efedea',
    fgNegativeDefault: '#440707',
    borderNeutralDefault: '#d5d3d3',
    borderNeutralSubtle: '#dedcda',
    borderNeutralHighlight: '#ffffff',
    borderNeutralFocus: '#6c7ba8',
    borderNeutralStrong: '#c8c7c7',
    neutralXs: 'rgba(19,24,58,.08)',
    neutralSm: 'rgba(19,24,58,.12)',
    neutralXl: 'rgba(19,24,58,.56)',
    accentXs: 'rgba(0,23,135,.08)',
    accentMd: 'rgba(0,23,135,.16)',
    accentXl: 'rgba(0,23,135,.56)',
    negativeXs: 'rgba(127,18,17,.08)',
    negativeMd: 'rgba(127,18,17,.16)',
    negativeXl: 'rgba(127,18,17,.56)',
    blue700: '#2467eb',
    blue900: '#0d4dcb',
    danger: '#7f1211',
    fgDangerInverse: '#efe0dd',
    radiusSmall: '6px',
    radiusLarge: '8px',
    radiusLarge1: '12px',
    radiusFull: '999px',
}

const shadow = {
    elevationOutline: `0 0 0 1px ${DS.neutralXs}, 0 1px 2px ${DS.neutralXs}, inset 0 0 0 1px ${DS.borderNeutralHighlight}`,
    inputStrong: `0 0 0 1px ${DS.neutralXl}, 0 1px 2px ${DS.neutralXs}, inset 0 0 0 1px ${DS.borderNeutralHighlight}`,
    inputInteractive: `0 0 0 4px ${DS.accentXs}, 0 0 0 1px ${DS.accentXl}, 0 3px 6px ${DS.accentMd}`,
    inputNegative: `0 0 0 4px ${DS.negativeXs}, 0 0 0 4px ${DS.bgBaseDefault}, 0 0 0 1px ${DS.negativeXl}, 0 3px 6px ${DS.negativeMd}`,
    focusOffset: `0 0 0 4px ${DS.borderNeutralFocus}, 0 0 0 2px ${DS.bgBaseDefault}, 0 1px 2px ${DS.neutralSm}`,
    elevationRaised: `0 1px 2px ${DS.blue900}`,
}

export default {
    baseStyle: () => {
        const hidePassword = getAssetUrl('static/img/form/hide-password.svg')
        const showPassword = getAssetUrl('static/img/form/show-password.svg')
        const closeModal = getAssetUrl('static/img/form/close-modal.svg')
        const semantics = getSemantics(BRAND_KEY)
        const buttonRadius =
            BRAND_KEY === 'delonghi'
                ? DS.radiusFull
                : (semantics.radii.button.radius['primary-default'] as Responsive)?.base ??
                  DS.radiusFull

        return {
            '.chakra-ui-light': {
                '.gigya-password-strength-bubble .gig-balloon-frame': {
                    background: `${DS.danger} !important`,
                    border: '0 !important',
                    borderRadius: `${DS.radiusSmall} !important`,
                    boxShadow: '0 0 2px rgba(0,0,0,.15) !important',
                    padding: remCalc(8),
                    maxWidth: remCalc(268),
                    minWidth: remCalc(226),
                },
                '.gigya-password-strength-bubble .gig-balloon-frame:has(.gigya-passwordStrength-bar-strong), .gigya-password-strength-bubble .gig-balloon-frame:has(.gigya-passwordStrength-bar-very_strong)':
                    {
                        background: `${DS.bgAccentDefault} !important`,
                    },
                '.gigya-password-strength-bubble .gig-balloon-nub, .gigya-password-strength-bubble .gig-balloon-nub-up, .gigya-password-strength-bubble .gig-balloon-nub-right, .gigya-password-strength-bubble .gig-balloon-nub-down':
                    {
                        marginLeft: 'calc(50% - 16px)',
                    },
                '.gigya-password-strength-bubble .gig-balloon-frame .gigya-passwordStrength-bar': {
                    display: 'none',
                },
                '.gigya-passwordStrength-text:not(.gigya-passwordStrength-text .gigya-passwordStrength-text)':
                    {
                        paddingBottom: remCalc(4),
                        fontSize: remCalc(12),
                        fontWeight: 500,
                        lineHeight: '1.2',
                        letterSpacing: '-0.06px',
                        color: DS.fgDangerInverse,
                    },
                '.gigya-passwordStrength-text, div.gigya-passwordStrength-text-requirements': {
                    textAlign: 'left',
                    fontSize: remCalc(12),
                    fontWeight: 400,
                    lineHeight: '1.2',
                    letterSpacing: '-0.06px',
                    color: DS.fgDangerInverse,
                    opacity: 0.9,
                },
                '.gigya-password-strength-bubble .gig-balloon-frame, .gigya-password-strength-bubble .gig-balloon-frame *':
                    {
                        color: `${DS.fgDangerInverse} !important`,
                    },
                'span.gigya-passwordStrength-scoreText': {
                    marginLeft: 0,
                },

                '.gigya-style-modern #gigya-screen-dialog-page-overlay': {
                    opacity: 1,
                    background: DS.bgBaseOverlay,
                },
                '.gigya-screen-dialog.gigya-style-modern, .gigya-screen-dialog-mobile.gigya-style-modern':
                    {
                        width: '100%',
                        left: '0 !important',
                        display: 'flex',
                        justifyContent: 'center',
                    },
                '.gigya-screen-dialog-mobile.gigya-style-modern': {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    zIndex: 1000,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '20px 0',
                    boxSizing: 'border-box',
                },
                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-main, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-main':
                    {
                        width: '94%',
                        maxWidth: remCalc(688),
                        padding: `${remCalc(24)} ${remCalc(24)}`,
                        background: DS.bgBaseDefault,
                        borderRadius: DS.radiusLarge1,
                        boxShadow: shadow.elevationOutline,
                    },
                '.gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-main': {
                    flexShrink: 0,
                },
                '.gigya-screen-dialog.gigya-style-modern .dlg-screen-dialog-main .gigya-screen-dialog-top, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-screen-dialog-main .gigya-screen-dialog-top':
                    {
                        display: 'none',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-top, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-top':
                    {
                        padding: `0 0 ${remCalc(16)}`,
                        background: 'transparent',
                        borderBottom: `1px solid ${DS.borderNeutralSubtle}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: remCalc(24),
                    },
                '.gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-top': {
                    position: 'relative',
                },
                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-top .gigya-screen-dialog-caption, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-top .gigya-screen-dialog-caption':
                    {
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(18),
                        lineHeight: remCalc(22),
                        fontWeight: 400,
                        fontFamily: 'inherit',
                    },
                '.gigya-style-modern div.gigya-screen-dialog-close > a': {
                    display: 'flex',
                    padding: remCalc(4),
                    color: DS.fgNeutralDefault,
                    outline: 'none',
                    borderRadius: DS.radiusLarge,
                    width: remCalc(24),
                    height: remCalc(24),
                    boxSizing: 'content-box',
                    background: `url(${closeModal}) center / ${remCalc(24)} ${remCalc(
                        24,
                    )} no-repeat`,
                },
                '.gigya-style-modern div.gigya-screen-dialog-close > a > img, .gigya-style-modern div.gigya-screen-dialog-close > a > span':
                    {
                        display: 'none',
                    },
                '.gigya-style-modern div.gigya-screen-dialog-close > a:focus-visible': {
                    outline: `2px solid ${DS.borderNeutralFocus}`,
                    outlineOffset: '2px',
                },

                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-inner, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-inner':
                    {
                        background: 'transparent !important',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-inner .gigya-screen-content form, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-inner .gigya-screen-content form':
                    {
                        paddingTop: 0,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-screen-dialog-inner .gigya-screen-content form>.gigya-layout-row, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-screen-dialog-inner .gigya-screen-content form>.gigya-layout-row':
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-label-text.gigya-composite-control, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-label-text.gigya-composite-control':
                    {
                        paddingBottom: remCalc(24),
                        fontSize: remCalc(16),
                        lineHeight: remCalc(20),
                        color: DS.fgNeutralDefault,
                        fontWeight: 400,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control':
                    {
                        position: 'relative',
                    },

                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input':
                    {
                        borderRadius: `${DS.radiusLarge1} !important`,
                        border: `1px solid ${DS.borderNeutralHighlight} !important`,
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(14),
                        lineHeight: remCalc(18),
                        fontWeight: 400,
                        outline: '0',
                        width: '100%',
                        height: remCalc(44),
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        padding: `${remCalc(8)} ${remCalc(12)}`,
                        boxSizing: 'border-box',
                        appearance: 'none',
                        boxShadow: shadow.elevationOutline,
                        transition: 'box-shadow .15s ease, background-color .15s ease',
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill, .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus':
                    {
                        WebkitBoxShadow: `0 0 0 1000px ${DS.bgBaseElevated} inset, ${shadow.elevationOutline} !important`,
                        WebkitTextFillColor: `${DS.fgNeutralDefault} !important`,
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input::placeholder, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input::placeholder':
                    {
                        color: DS.fgNeutralSubtle,
                        opacity: 1,
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input:hover, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input:hover':
                    {
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input:focus, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input:focus':
                    {
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        boxShadow: `${shadow.inputInteractive} !important`,
                        padding: `${remCalc(8)} ${remCalc(12)} 0`,
                        outline: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-show-password-button, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-show-password-button':
                    {
                        position: 'absolute',
                        right: remCalc(12),
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: remCalc(20),
                        height: remCalc(20),
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: DS.fgNeutralSubtle,
                        zIndex: 2,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, .gigya-screen-dialog.gigya-style-modern .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon':
                    {
                        content: `url("${hidePassword}")`,
                        display: 'block',
                        width: remCalc(16),
                        height: remCalc(16),
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, .gigya-screen-dialog.gigya-style-modern .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon':
                    {
                        content: `url("${showPassword}")`,
                        display: 'block',
                        width: remCalc(16),
                        height: remCalc(16),
                    },

                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper>label, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper>label':
                    {
                        display: 'inline-block',
                        color: DS.fgNeutralSubtle,
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: remCalc(12),
                        right: remCalc(12),
                        zIndex: '1',
                        fontSize: remCalc(14),
                        lineHeight: remCalc(18),
                        backgroundColor: 'transparent',
                        transition: 'top .2s ease, transform .2s ease, font-size .2s ease',
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input:focus)>label, .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.focus-visible)>label, .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.gigya-valid)>label, .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.gigya-error)>label, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input:focus)>label, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.focus-visible)>label, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.gigya-valid)>label, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper:has(input.gigya-error)>label':
                    {
                        top: remCalc(6),
                        transform: 'none',
                        fontSize: remCalc(10),
                        lineHeight: '1.2',
                        letterSpacing: '-0.05px',
                        backgroundColor: 'transparent',
                        color: DS.fgNeutralSubtle,
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper>label .gigya-label-text, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper>label .gigya-label-text':
                    {
                        color: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-error-msg, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-error-msg':
                    {
                        fontSize: remCalc(12),
                        lineHeight: '1.2',
                        letterSpacing: '-0.06px',
                        color: DS.fgNegativeDefault,
                        textAlign: 'left',
                        marginTop: remCalc(8),
                        minHeight: 'auto',
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper input.gigya-error, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper input.gigya-error':
                    {
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        boxShadow: `${shadow.inputNegative} !important`,
                        border: `1px solid ${DS.borderNeutralHighlight} !important`,
                        color: DS.fgNegativeDefault,
                        padding: `${remCalc(8)} ${remCalc(12)} 0`,
                    },
                '.gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper>label:has(~ .gigya-error), .gigya-screen-dialog-mobile.gigya-style-modern .dlg-gigya-input-text-wrapper>label:has(~ .gigya-error), .gigya-screen-dialog.gigya-style-modern .dlg-gigya-input-text-wrapper>label:has(~ .gigya-error) *':
                    {
                        color: DS.fgNegativeDefault,
                    },

                '.gigya-screen-dialog.gigya-style-modern .dlg-hidden, .gigya-screen-dialog.gigya-style-modern .gigya-toggle-content, .gigya-screen-dialog-mobile.gigya-style-modern .dlg-hidden, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-toggle-content':
                    {
                        display: 'none',
                    },

                '.gigya-screen-dialog.gigya-style-modern .dln-completeForm__paragraph--secondary, .gigya-screen-dialog-mobile.gigya-style-modern .dln-completeForm__paragraph--secondary':
                    {
                        fontSize: remCalc(16),
                        fontWeight: 400,
                        color: DS.fgNeutralDefault,
                        lineHeight: '1.25',
                        marginBottom: remCalc(24),
                        display: 'inline-block',
                    },
                '.gigya-screen-dialog.gigya-style-modern .dln-completeForm__paragraph a, .gigya-screen-dialog.gigya-style-modern .gigya-checkbox-text a, .gigya-screen-dialog-mobile.gigya-style-modern .dln-completeForm__paragraph a, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-checkbox-text a':
                    {
                        textDecoration: 'underline',
                        color: DS.fgNeutralDefault,
                        fontWeight: 500,
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-input-submit, .gigya-screen-dialog.gigya-style-modern .gigya-button-link, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-submit, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-button-link':
                    {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        outlineWidth: 0,
                        fontSize: remCalc(16),
                        lineHeight: '1.15',
                        letterSpacing: '-0.16px',
                        fontWeight: 500,
                        height: remCalc(40),
                        minHeight: remCalc(40),
                        background: DS.bgAccentDefault,
                        borderRadius: buttonRadius,
                        color: DS.fgNeutralInverse,
                        cursor: 'pointer',
                        marginTop: remCalc(24),
                        padding: `${remCalc(12)} ${remCalc(24)}`,
                        border: 'none',
                        boxShadow: 'none',
                        transition: 'background .15s ease',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-button-link button, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-button-link button':
                    {
                        fontWeight: 500,
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        fontSize: 'inherit',
                        cursor: 'pointer',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-submit:hover, .gigya-screen-dialog.gigya-style-modern .gigya-button-link:hover, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-submit:hover, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-button-link:hover':
                    {
                        background: DS.bgAccentHover,
                        color: DS.fgNeutralInverse,
                        border: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-submit:focus-visible, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-submit:focus-visible':
                    {
                        boxShadow: shadow.focusOffset,
                        outline: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-submit:disabled, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-submit:disabled':
                    {
                        background: DS.bgAccentMuted,
                        color: DS.fgNeutralInverse,
                        cursor: 'not-allowed',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-link, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-link':
                    {
                        marginTop: remCalc(16),
                        textAlign: 'center',
                        color: DS.fgNeutralMuted,
                        fontSize: remCalc(14),
                        fontWeight: 500,
                        lineHeight: remCalc(18),
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-link:hover, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-link:hover':
                    {
                        color: DS.fgNeutralDefault,
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-resend-verification-code-form .gigya-spacer, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-resend-verification-code-form .gigya-spacer':
                    {
                        display: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-resend-verification-code-form .gigya-composite-control-submit input, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-resend-verification-code-form .gigya-composite-control-submit input':
                    {
                        marginTop: 0,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-resend-verification-code-form .gigya-composite-control-link, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-resend-verification-code-form .gigya-composite-control-link':
                    {
                        marginTop: remCalc(24),
                        fontWeight: 500,
                        textAlign: 'center',
                        color: DS.fgNeutralMuted,
                        textDecoration: 'underline',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox':
                    {
                        position: 'absolute',
                        opacity: 0,
                        width: '1px',
                        height: '1px',
                        margin: '-1px',
                        padding: 0,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        border: 0,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox+label.gigya-label, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox+label.gigya-label':
                    {
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        paddingLeft: remCalc(24),
                        gap: remCalc(8),
                        minHeight: remCalc(16),
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox+label.gigya-label:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox+label.gigya-label:before':
                    {
                        content: '""',
                        position: 'absolute',
                        display: 'block',
                        top: 0,
                        left: 0,
                        width: remCalc(16),
                        height: remCalc(16),
                        borderRadius: remCalc(4),
                        backgroundColor: DS.bgBaseDefault,
                        boxShadow: shadow.inputStrong,
                        border: `1px solid ${DS.borderNeutralHighlight}`,
                        flexShrink: 0,
                        zIndex: 1,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox:not(:disabled)+label.gigya-label:hover:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox:not(:disabled)+label.gigya-label:hover:before':
                    {
                        backgroundColor: DS.bgBaseElevated,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox:focus-visible+label.gigya-label:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox:focus-visible+label.gigya-label:before':
                    {
                        boxShadow: shadow.inputInteractive,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox:checked+label.gigya-label:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox:checked+label.gigya-label:before':
                    {
                        backgroundColor: DS.blue700,
                        boxShadow: shadow.elevationRaised,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox+label.gigya-label:after, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox+label.gigya-label:after':
                    {
                        content: '""',
                        position: 'absolute',
                        display: 'none',
                        top: remCalc(0),
                        left: remCalc(5),
                        width: remCalc(4),
                        height: remCalc(11),
                        border: `solid ${DS.fgNeutralInverse}`,
                        borderWidth: `0 2px 2px 0`,
                        transform: 'rotate(45deg)',
                        zIndex: 2,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox:checked+label.gigya-label:after, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox:checked+label.gigya-label:after':
                    {
                        display: 'block',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-input-checkbox:disabled+label.gigya-label:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-input-checkbox:disabled+label.gigya-label:before':
                    {
                        backgroundColor: DS.bgBaseUnder,
                        boxShadow: shadow.elevationOutline,
                        border: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-checkbox.gigya-error .gigya-input-checkbox+label.gigya-label:before, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-checkbox.gigya-error .gigya-input-checkbox+label.gigya-label:before':
                    {
                        boxShadow: shadow.inputNegative,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-checkbox .gigya-checkbox-text, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-checkbox .gigya-checkbox-text, #login .gigya-composite-control-checkbox .gigya-checkbox-text, #register .gigya-composite-control-checkbox .gigya-checkbox-text, #gigya-login-popup-screen-container .gigya-composite-control-checkbox .gigya-checkbox-text, #guest-login .gigya-composite-control-checkbox .gigya-checkbox-text':
                    {
                        fontSize: remCalc(14),
                        lineHeight: remCalc(16),
                        fontWeight: 500,
                        color: DS.fgNeutralDefault,
                        display: 'block',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="facebook"] > button > div, .gigya-screen-dialog.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="googleplus"] > button > div, .gigya-screen-dialog.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="google"] > button > div, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="facebook"] > button > div, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="googleplus"] > button > div, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-providers-list-container [data-gigya-provider="google"] > button > div':
                    {
                        mixBlendMode: 'multiply',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-composite-control-checkbox .gigya-checkbox-text button.dln-icn.dln-icn--iconfont, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-composite-control-checkbox .gigya-checkbox-text button.dln-icn.dln-icn--iconfont':
                    {
                        display: 'none',
                    },

                '.gigya-screen-dialog.gigya-style-modern .gigya-login-providers-container, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-providers-container':
                    {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: remCalc(24),
                        width: '100%',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row':
                    {
                        flex: '1 0 0',
                        minWidth: 0,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row .gigya-login-provider, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row .gigya-login-provider':
                    {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: remCalc(8),
                        width: '100%',
                        height: remCalc(40),
                        padding: `${remCalc(12)} ${remCalc(24)}`,
                        background: DS.bgBaseOver,
                        border: 'none',
                        borderRadius: DS.radiusFull,
                        boxShadow: shadow.elevationOutline,
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(16),
                        fontWeight: 500,
                        fontFamily: 'inherit',
                        lineHeight: '1.15',
                        letterSpacing: '-0.16px',
                        cursor: 'pointer',
                        transition: 'background .15s ease',
                        textDecoration: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row .gigya-login-provider:hover, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row .gigya-login-provider:hover':
                    {
                        background: DS.bgBaseUnder,
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row .gigya-login-provider:focus-visible, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row .gigya-login-provider:focus-visible':
                    {
                        background: DS.bgBaseElevated,
                        boxShadow: shadow.focusOffset,
                        outline: 'none',
                    },
                '.gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row .gigya-login-provider img, .gigya-screen-dialog.gigya-style-modern .gigya-login-provider-row .gigya-login-provider svg, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row .gigya-login-provider img, .gigya-screen-dialog-mobile.gigya-style-modern .gigya-login-provider-row .gigya-login-provider svg':
                    {
                        width: remCalc(16),
                        height: remCalc(16),
                        flexShrink: 0,
                    },

                '@media (min-width: 1024px)': {
                    '.gigya-screen-dialog-mobile.gigya-style-modern': {
                        padding: `${remCalc(40)} 0`,
                    },
                    '#login .gigya-login-provider-row, #register .gigya-login-provider-row, #gigya-login-popup-screen-container .gigya-login-provider-row, #guest-login .gigya-login-provider-row':
                        {
                            gap: remCalc(24),
                        },
                },

                '#login .gigya-screen.v2, #register .gigya-screen.v2, #reset-password .gigya-screen.v2, #profile-update .gigya-screen.v2, #password-update .gigya-screen.v2, #complete-registration .gigya-screen.v2, #gigya-forgotten-password-container .gigya-screen.v2, #gigya-login-popup-screen-container .gigya-screen.v2, #guest-login .gigya-screen.v2':
                    {
                        background: 'transparent',
                    },
                '#login form, #register form, #reset-password form, #profile-update form, #password-update form, #complete-registration form, #gigya-forgotten-password-container form, #gigya-login-popup-screen-container form, #guest-login form':
                    {
                        paddingTop: 0,
                    },
                '#login form .gigya-layout-row, #gigya-login-popup-screen-container form .gigya-layout-row, #guest-login form .gigya-layout-row':
                    {
                        display: 'flex',
                        flexDirection: 'column',
                    },
                '#login form .gigya-layout-row > .gigya-composite-control-social-login, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-composite-control-social-login, #guest-login form .gigya-layout-row > .gigya-composite-control-social-login':
                    {
                        order: 10,
                        paddingTop: 0,
                    },
                '#login form .gigya-layout-row > .gigya-login-spacer, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-login-spacer, #guest-login form .gigya-layout-row > .gigya-login-spacer':
                    {
                        order: 9,
                    },
                '#login form .gigya-layout-row > .dln-loginForm__paragraph, #gigya-login-popup-screen-container form .gigya-layout-row > .dln-loginForm__paragraph, #guest-login form .gigya-layout-row > .dln-loginForm__paragraph':
                    {
                        order: 3,
                    },
                '#login .required-fields-label, #gigya-login-popup-screen-container .required-fields-label, #guest-login .required-fields-label':
                    {
                        display: 'none',
                    },
                '#login form .gigya-layout-row > .gigya-composite-control-textbox, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-composite-control-textbox, #guest-login form .gigya-layout-row > .gigya-composite-control-textbox':
                    {
                        order: 4,
                    },
                '#login form .gigya-layout-row > .gigya-composite-control-password, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-composite-control-password, #guest-login form .gigya-layout-row > .gigya-composite-control-password':
                    {
                        order: 5,
                    },
                '#login form .gigya-layout-row > .gigya-composite-control-form-error, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-composite-control-form-error, #guest-login form .gigya-layout-row > .gigya-composite-control-form-error':
                    {
                        order: 6,
                        fontSize: remCalc(12),
                        lineHeight: '1.2',
                        color: DS.fgNegativeDefault,
                    },
                '#login form .gigya-layout-row > .gigya-composite-control-link, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-composite-control-link, #guest-login form .gigya-layout-row > .gigya-composite-control-link':
                    {
                        order: 7,
                        textAlign: 'right',
                        marginTop: remCalc(8),
                    },
                '#login a.gigya-forgotPassword, #login .gigya-composite-control-link a, #gigya-login-popup-screen-container a.gigya-forgotPassword, #gigya-login-popup-screen-container .gigya-composite-control-link a, #guest-login a.gigya-forgotPassword, #guest-login .gigya-composite-control-link a':
                    {
                        color: `${DS.fgNeutralMuted} !important`,
                        textDecoration: 'underline !important',
                        fontSize: remCalc(12),
                        fontWeight: 500,
                        lineHeight: '1.2',
                        letterSpacing: '-0.06px',
                        display: 'inline-block',
                        padding: `${remCalc(4)} 0`,
                        height: remCalc(28),
                        textUnderlineOffset: remCalc(4),
                    },
                '#login a.gigya-forgotPassword:hover, #login .gigya-composite-control-link a:hover, #gigya-login-popup-screen-container a.gigya-forgotPassword:hover, #gigya-login-popup-screen-container .gigya-composite-control-link a:hover, #guest-login a.gigya-forgotPassword:hover, #guest-login .gigya-composite-control-link a:hover':
                    {
                        textDecoration: 'underline !important',
                    },
                '#login form .gigya-layout-row > .gigya-clear-spacer, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-clear-spacer, #guest-login form .gigya-layout-row > .gigya-clear-spacer':
                    {
                        order: 8,
                    },
                '#login form .gigya-layout-row > .gigya-layout-row.with-divider, #gigya-login-popup-screen-container form .gigya-layout-row > .gigya-layout-row.with-divider, #guest-login form .gigya-layout-row > .gigya-layout-row.with-divider':
                    {
                        order: 8,
                    },
                '#login-subtitle': {
                    fontSize: remCalc(16),
                    lineHeight: remCalc(20),
                    color: DS.fgNeutralDefault,
                    fontWeight: 400,
                    margin: `0 0 ${remCalc(16)} 0`,
                    padding: 0,
                },
                '#login #gigya-login-screen h2.dln-registration__subtitle, #gigya-login-popup-screen-container #gigya-login-screen h2.dln-registration__subtitle, #guest-login #gigya-login-screen h2.dln-registration__subtitle':
                    {
                        color: DS.fgNeutralDefault,
                    },
                '#login-social-separator': {
                    order: 9,
                    width: '100%',
                    textAlign: 'left',
                    fontSize: remCalc(16),
                    fontWeight: 400,
                    lineHeight: remCalc(20),
                    color: DS.fgNeutralDefault,
                    paddingTop: remCalc(80),
                    paddingBottom: remCalc(16),
                    margin: 0,
                },
                '#login .gigya-label-text.gigya-composite-control, #register .gigya-label-text.gigya-composite-control, #complete-registration .gigya-label-text.gigya-composite-control, #gigya-login-popup-screen-container .gigya-label-text.gigya-composite-control, #guest-login .gigya-label-text.gigya-composite-control':
                    {
                        paddingBottom: remCalc(24),
                        fontSize: remCalc(16),
                        color: DS.fgNeutralDefault,
                        fontWeight: 400,
                    },
                '#login .gigya-composite-control, #register .gigya-composite-control, #reset-password .gigya-composite-control, #profile-update .gigya-composite-control, #password-update .gigya-composite-control, #gigya-forgotten-password-container .gigya-composite-control, #gigya-login-popup-screen-container .gigya-composite-control, #guest-login .gigya-composite-control':
                    {
                        position: 'relative',
                    },
                '#login .gigya-composite-control.dln-loginForm__paragraph, #register .gigya-composite-control.dln-loginForm__paragraph, #gigya-login-popup-screen-container .gigya-composite-control.dln-loginForm__paragraph, #guest-login .gigya-composite-control.dln-loginForm__paragraph':
                    {
                        marginBottom: `${remCalc(8)} !important`,
                    },
                '#login .gigya-composite-control-textbox, #register .gigya-composite-control-textbox, #gigya-login-popup-screen-container .gigya-composite-control-textbox, #guest-login .gigya-composite-control-textbox':
                    {
                        marginBottom: `${remCalc(8)} !important`,
                    },
                '#register .gigya-composite-control-textbox': {
                    marginBottom: `${remCalc(16)} !important`,
                },
                '#login .gigya-composite-control-password, #register .gigya-composite-control-password, #gigya-login-popup-screen-container .gigya-composite-control-password, #guest-login .gigya-composite-control-password':
                    {
                        marginBottom: '0 !important',
                    },
                '#login .dlg-gigya-input-text-wrapper input, #register .dlg-gigya-input-text-wrapper input, #reset-password .dlg-gigya-input-text-wrapper input, #profile-update .dlg-gigya-input-text-wrapper input, #password-update .dlg-gigya-input-text-wrapper input, #reset-password input[type="password"], #profile-update input[type="password"], #password-update input[type="password"], #reset-password input[type="text"], #profile-update input[type="text"], #password-update input[type="text"], #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input, #gigya-forgotten-password-container input[type="text"], #gigya-forgotten-password-container input[type="email"], #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input, #guest-login .dlg-gigya-input-text-wrapper input':
                    {
                        borderRadius: `${DS.radiusLarge1} !important`,
                        border: `1px solid ${DS.borderNeutralHighlight} !important`,
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(14),
                        lineHeight: remCalc(18),
                        fontWeight: 400,
                        height: remCalc(44),
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        padding: `${remCalc(8)} ${remCalc(12)}`,
                        boxShadow: `${shadow.elevationOutline} !important`,
                        boxSizing: 'border-box',
                        width: '100%',
                    },
                '#login .dlg-gigya-input-text-wrapper input:-webkit-autofill, #login .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, #login .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus, #register .dlg-gigya-input-text-wrapper input:-webkit-autofill, #register .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, #register .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus, #reset-password .dlg-gigya-input-text-wrapper input:-webkit-autofill, #profile-update .dlg-gigya-input-text-wrapper input:-webkit-autofill, #password-update .dlg-gigya-input-text-wrapper input:-webkit-autofill, #reset-password input[type="password"]:-webkit-autofill, #profile-update input[type="password"]:-webkit-autofill, #password-update input[type="password"]:-webkit-autofill, #reset-password input[type="text"]:-webkit-autofill, #profile-update input[type="text"]:-webkit-autofill, #password-update input[type="text"]:-webkit-autofill, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input:-webkit-autofill, #gigya-forgotten-password-container input[type="text"]:-webkit-autofill, #gigya-forgotten-password-container input[type="email"]:-webkit-autofill, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input:-webkit-autofill, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus, #guest-login .dlg-gigya-input-text-wrapper input:-webkit-autofill, #guest-login .dlg-gigya-input-text-wrapper input:-webkit-autofill:hover, #guest-login .dlg-gigya-input-text-wrapper input:-webkit-autofill:focus':
                    {
                        WebkitBoxShadow: `0 0 0 1000px ${DS.bgBaseElevated} inset, ${shadow.elevationOutline} !important`,
                        WebkitTextFillColor: `${DS.fgNeutralDefault} !important`,
                    },
                '#login .dlg-gigya-input-text-wrapper input::placeholder, #register .dlg-gigya-input-text-wrapper input::placeholder, #reset-password .dlg-gigya-input-text-wrapper input::placeholder, #profile-update .dlg-gigya-input-text-wrapper input::placeholder, #password-update .dlg-gigya-input-text-wrapper input::placeholder, #reset-password input[type="password"]::placeholder, #profile-update input[type="password"]::placeholder, #password-update input[type="password"]::placeholder, #reset-password input[type="text"]::placeholder, #profile-update input[type="text"]::placeholder, #password-update input[type="text"]::placeholder, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input::placeholder, #gigya-forgotten-password-container input[type="text"]::placeholder, #gigya-forgotten-password-container input[type="email"]::placeholder, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input::placeholder, #guest-login .dlg-gigya-input-text-wrapper input::placeholder':
                    {
                        color: DS.fgNeutralMuted,
                        opacity: 1,
                    },
                '#login .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #register .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #reset-password .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #profile-update .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #password-update .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder, #guest-login .dlg-gigya-input-text-wrapper:not(:has(input:focus)) input:placeholder-shown::placeholder':
                    {
                        opacity: 0,
                    },
                '#login .dlg-gigya-input-text-wrapper input:hover, #register .dlg-gigya-input-text-wrapper input:hover, #reset-password .dlg-gigya-input-text-wrapper input:hover, #profile-update .dlg-gigya-input-text-wrapper input:hover, #password-update .dlg-gigya-input-text-wrapper input:hover, #reset-password input[type="password"]:hover, #profile-update input[type="password"]:hover, #password-update input[type="password"]:hover, #reset-password input[type="text"]:hover, #profile-update input[type="text"]:hover, #password-update input[type="text"]:hover, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input:hover, #gigya-forgotten-password-container input[type="text"]:hover, #gigya-forgotten-password-container input[type="email"]:hover, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input:hover, #guest-login .dlg-gigya-input-text-wrapper input:hover':
                    {
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                    },
                '#login .dlg-gigya-input-text-wrapper input:focus, #register .dlg-gigya-input-text-wrapper input:focus, #reset-password .dlg-gigya-input-text-wrapper input:focus, #profile-update .dlg-gigya-input-text-wrapper input:focus, #password-update .dlg-gigya-input-text-wrapper input:focus, #reset-password input[type="password"]:focus, #profile-update input[type="password"]:focus, #password-update input[type="password"]:focus, #reset-password input[type="text"]:focus, #profile-update input[type="text"]:focus, #password-update input[type="text"]:focus, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input:focus, #gigya-forgotten-password-container input[type="text"]:focus, #gigya-forgotten-password-container input[type="email"]:focus, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input:focus, #guest-login .dlg-gigya-input-text-wrapper input:focus':
                    {
                        boxShadow: `${shadow.inputInteractive} !important`,
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        outline: 'none',
                    },
                '#login .dlg-gigya-input-text-wrapper:has(input:focus) input, #login .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #login .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #register .dlg-gigya-input-text-wrapper:has(input:focus) input, #register .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #register .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #reset-password .dlg-gigya-input-text-wrapper:has(input:focus) input, #profile-update .dlg-gigya-input-text-wrapper:has(input:focus) input, #password-update .dlg-gigya-input-text-wrapper:has(input:focus) input, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:focus) input, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #login .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #register .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #reset-password .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #profile-update .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #password-update .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:focus) input, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input, #guest-login .dlg-gigya-input-text-wrapper:has(input:focus) input, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-valid) input, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-error) input, #guest-login .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) input':
                    {
                        padding: `${remCalc(18)} ${remCalc(12)} ${remCalc(4)} !important`,
                    },
                '#login .dlg-gigya-input-text-wrapper > label, #register .dlg-gigya-input-text-wrapper > label, #reset-password .dlg-gigya-input-text-wrapper > label, #profile-update .dlg-gigya-input-text-wrapper > label, #password-update .dlg-gigya-input-text-wrapper > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper > label, #guest-login .dlg-gigya-input-text-wrapper > label':
                    {
                        position: 'absolute',
                        top: remCalc(22),
                        left: remCalc(12),
                        transform: 'translateY(-50%)',
                        color: DS.fgNeutralMuted,
                        fontSize: remCalc(14),
                        fontWeight: 400,
                        pointerEvents: 'none',
                        transition: 'all .15s ease',
                        zIndex: 1,
                        margin: 0,
                        padding: 0,
                        lineHeight: 1,
                    },
                '#login .dlg-gigya-input-text-wrapper:has(input:focus) > label, #login .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #login .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #register .dlg-gigya-input-text-wrapper:has(input:focus) > label, #register .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #register .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #register .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input:focus) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input:focus) > label, #password-update .dlg-gigya-input-text-wrapper:has(input:focus) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #password-update .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:focus) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #login .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #register .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #password-update .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:focus) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input:focus) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input.focus-visible) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-valid) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input:-webkit-autofill) > label':
                    {
                        top: remCalc(8),
                        fontSize: remCalc(10),
                        color: DS.fgNeutralSubtle,
                        transform: 'none',
                        background: 'transparent !important',
                    },
                '#login .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #login .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #register .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #register .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #password-update .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #password-update .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-valid) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input:focus):has(input.gigya-error) > label':
                    {
                        top: remCalc(8),
                        fontSize: remCalc(10),
                        transform: 'none',
                    },
                '#login .dlg-gigya-input-text-wrapper > label .gigya-label-text, #register .dlg-gigya-input-text-wrapper > label .gigya-label-text, #reset-password .dlg-gigya-input-text-wrapper > label .gigya-label-text, #profile-update .dlg-gigya-input-text-wrapper > label .gigya-label-text, #password-update .dlg-gigya-input-text-wrapper > label .gigya-label-text, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper > label .gigya-label-text, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper > label .gigya-label-text, #guest-login .dlg-gigya-input-text-wrapper > label .gigya-label-text':
                    {
                        fontSize: 'inherit',
                        color: 'inherit',
                    },
                '#login .gigya-show-password-button, #register .gigya-show-password-button, #gigya-forgotten-password-container .gigya-show-password-button, #gigya-login-popup-screen-container .gigya-show-password-button, #guest-login .gigya-show-password-button':
                    {
                        position: 'absolute',
                        right: remCalc(12),
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: remCalc(20),
                        height: remCalc(20),
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: DS.fgNeutralSubtle,
                        zIndex: 2,
                    },
                '#login .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, #login .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon, #register .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, #register .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon, #gigya-forgotten-password-container .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, #gigya-forgotten-password-container .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon, #gigya-login-popup-screen-container .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, #gigya-login-popup-screen-container .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon, #guest-login .gigya-composite-control-password-peek input[type=password] + .gigya-show-password-button .gigya-password-icon, #guest-login .gigya-composite-control-password-peek[data-show-password=false] .gigya-password-icon':
                    {
                        content: `url("${hidePassword}")`,
                        display: 'block',
                        width: remCalc(16),
                        height: remCalc(16),
                    },
                '#login .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, #login .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon, #register .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, #register .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon, #gigya-forgotten-password-container .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, #gigya-forgotten-password-container .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon, #gigya-login-popup-screen-container .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, #gigya-login-popup-screen-container .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon, #guest-login .gigya-composite-control-password-peek input[type=text] + .gigya-show-password-button .gigya-password-icon, #guest-login .gigya-composite-control-password-peek[data-show-password=true] .gigya-password-icon':
                    {
                        content: `url("${showPassword}")`,
                        display: 'block',
                        width: remCalc(16),
                        height: remCalc(16),
                    },
                '#login .gigya-error-msg, #register .gigya-error-msg, #reset-password .gigya-error-msg, #profile-update .gigya-error-msg, #password-update .gigya-error-msg, #gigya-forgotten-password-container .gigya-error-msg, #gigya-login-popup-screen-container .gigya-error-msg, #guest-login .gigya-error-msg':
                    {
                        fontSize: remCalc(12),
                        lineHeight: '1.2',
                        letterSpacing: '-0.06px',
                        color: DS.fgNegativeDefault,
                        marginTop: remCalc(8),
                        minHeight: 'auto',
                    },
                '#login .gigya-error-display, #register .gigya-error-display, #gigya-login-popup-screen-container .gigya-error-display, #guest-login .gigya-error-display':
                    {
                        order: 6,
                        marginTop: 0,
                        paddingTop: 0,
                        fontSize: remCalc(12),
                        lineHeight: '1.2',
                        color: DS.fgNegativeDefault,
                    },
                '#login .dlg-gigya-input-text-wrapper input.gigya-error, #register .dlg-gigya-input-text-wrapper input.gigya-error, #reset-password .dlg-gigya-input-text-wrapper input.gigya-error, #profile-update .dlg-gigya-input-text-wrapper input.gigya-error, #password-update .dlg-gigya-input-text-wrapper input.gigya-error, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper input.gigya-error, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper input.gigya-error, #guest-login .dlg-gigya-input-text-wrapper input.gigya-error':
                    {
                        boxShadow: `${shadow.inputNegative} !important`,
                        backgroundColor: `${DS.bgBaseElevated} !important`,
                        color: DS.fgNegativeDefault,
                        padding: `${remCalc(18)} ${remCalc(12)} ${remCalc(4)} !important`,
                    },
                '#login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #register .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #register .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #reset-password .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #profile-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #password-update .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #gigya-forgotten-password-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #gigya-login-popup-screen-container .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label, #guest-login .dlg-gigya-input-text-wrapper:has(input.gigya-error) > label *':
                    {
                        color: DS.fgNegativeDefault,
                    },
                '#login .gigya-input-submit, #login .gigya-button-link, #register .gigya-input-submit, #reset-password .gigya-input-submit, #profile-update .gigya-input-submit, #password-update .gigya-input-submit, #complete-registration .gigya-input-submit, #complete-registration .gigya-button-link, #gigya-forgotten-password-container .gigya-input-submit, #gigya-forgotten-password-container .gigya-button-link, #gigya-login-popup-screen-container .gigya-input-submit, #gigya-login-popup-screen-container .gigya-button-link, #guest-login .gigya-input-submit, #guest-login .gigya-button-link':
                    {
                        background: DS.bgAccentDefault,
                        color: DS.fgNeutralInverse,
                        borderRadius: buttonRadius,
                        border: 'none',
                        width: '100%',
                        padding: `${remCalc(12)} ${remCalc(24)}`,
                        fontSize: remCalc(16),
                        fontWeight: 500,
                        lineHeight: '1.15',
                        letterSpacing: '-0.16px',
                        cursor: 'pointer',
                        transition: 'background .15s ease',
                        textAlign: 'center',
                    },
                '#login .gigya-input-submit:hover, #login .gigya-button-link:hover, #register .gigya-input-submit:hover, #reset-password .gigya-input-submit:hover, #profile-update .gigya-input-submit:hover, #password-update .gigya-input-submit:hover, #complete-registration .gigya-input-submit:hover, #complete-registration .gigya-button-link:hover, #gigya-forgotten-password-container .gigya-input-submit:hover, #gigya-forgotten-password-container .gigya-button-link:hover, #gigya-login-popup-screen-container .gigya-input-submit:hover, #gigya-login-popup-screen-container .gigya-button-link:hover, #guest-login .gigya-input-submit:hover, #guest-login .gigya-button-link:hover':
                    {
                        background: DS.bgAccentHover,
                    },
                '#login .gigya-composite-control-social-login, #gigya-login-popup-screen-container .gigya-composite-control-social-login, #guest-login .gigya-composite-control-social-login':
                    {
                        width: '100%',
                    },
                '#login .gigya-social-login-container, #login [id^="login_social_"] > .gigya-login-providers, #gigya-login-popup-screen-container .gigya-social-login-container, #gigya-login-popup-screen-container [id^="login_social_"] > .gigya-login-providers, #guest-login .gigya-social-login-container, #guest-login [id^="login_social_"] > .gigya-login-providers':
                    {
                        width: '100% !important',
                    },
                '#login .gigya-login-providers-container, #login .gigya-login-providers-list-container, #login .gigya-login-providers-list-container td, #register .gigya-login-providers-container, #register .gigya-login-providers-list-container, #register .gigya-login-providers-list-container td, #gigya-login-popup-screen-container .gigya-login-providers-container, #gigya-login-popup-screen-container .gigya-login-providers-list-container, #gigya-login-popup-screen-container .gigya-login-providers-list-container td, #guest-login .gigya-login-providers-container, #guest-login .gigya-login-providers-list-container, #guest-login .gigya-login-providers-list-container td':
                    {
                        width: '100% !important',
                        display: 'block !important',
                        transform: 'none !important',
                    },
                '#login .gigya-login-providers, #register .gigya-login-providers, #gigya-login-popup-screen-container .gigya-login-providers, #guest-login .gigya-login-providers':
                    {
                        width: '100% !important',
                        height: 'auto !important',
                    },
                '#login [id^="login_social_"], #gigya-login-popup-screen-container [id^="login_social_"], #guest-login [id^="login_social_"]':
                    {
                        width: '100% !important',
                    },
                '#login .gigya-login-provider-row, #register .gigya-login-provider-row, #gigya-login-popup-screen-container .gigya-login-provider-row, #guest-login .gigya-login-provider-row':
                    {
                        display: 'flex !important',
                        gap: remCalc(16),
                        width: '100% !important',
                        height: 'auto !important',
                    },
                '#login .gigya-login-provider-row .gigya-login-provider, #register .gigya-login-provider-row .gigya-login-provider, #gigya-login-popup-screen-container .gigya-login-provider-row .gigya-login-provider, #guest-login .gigya-login-provider-row .gigya-login-provider':
                    {
                        display: 'inline-flex !important',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: remCalc(8),
                        flex: '1 1 0 !important',
                        width: 'auto !important',
                        minWidth: 0,
                        height: `${remCalc(40)} !important`,
                        padding: `${remCalc(8)} ${remCalc(12)}`,
                        background: DS.bgBaseUnder,
                        border: `1px solid ${DS.borderNeutralDefault}`,
                        borderRadius: DS.radiusLarge,
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(14),
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background .15s ease',
                        textDecoration: 'none',
                        boxSizing: 'border-box',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        marginRight: '0 !important',
                    },
                '#login .gigya-login-provider-row .gigya-login-provider:hover, #register .gigya-login-provider-row .gigya-login-provider:hover, #gigya-login-popup-screen-container .gigya-login-provider-row .gigya-login-provider:hover, #guest-login .gigya-login-provider-row .gigya-login-provider:hover':
                    {
                        background: DS.bgBaseElevated,
                    },
                '#login .gigya-login-provider-row .gigya-login-provider img, #login .gigya-login-provider-row .gigya-login-provider svg, #register .gigya-login-provider-row .gigya-login-provider img, #register .gigya-login-provider-row .gigya-login-provider svg, #gigya-login-popup-screen-container .gigya-login-provider-row .gigya-login-provider img, #gigya-login-popup-screen-container .gigya-login-provider-row .gigya-login-provider svg, #guest-login .gigya-login-provider-row .gigya-login-provider img, #guest-login .gigya-login-provider-row .gigya-login-provider svg':
                    {
                        width: remCalc(16),
                        height: remCalc(16),
                        flexShrink: 0,
                    },
                '#login-social-custom, #register-social-custom': {
                    display: 'flex',
                    gap: remCalc(24),
                    width: '100%',
                    marginTop: 0,
                },
                '#login .login-social-btn, #register .login-social-btn, #gigya-login-popup-screen-container .login-social-btn, #guest-login .login-social-btn':
                    {
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: remCalc(16),
                        flex: '1 1 0',
                        minWidth: 0,
                        height: remCalc(40),
                        padding: remCalc(12),
                        background: '#f2f2f2',
                        border: '1px solid #bdbdbd',
                        borderRadius: 0,
                        color: DS.fgNeutralDefault,
                        fontSize: remCalc(16),
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background .15s ease',
                        boxSizing: 'border-box',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        fontFamily: 'inherit',
                    },
                '#login .login-social-btn:hover, #register .login-social-btn:hover, #gigya-login-popup-screen-container .login-social-btn:hover, #guest-login .login-social-btn:hover':
                    {
                        background: DS.bgBaseElevated,
                    },
                '#login .login-social-btn svg, #register .login-social-btn svg, #gigya-login-popup-screen-container .login-social-btn svg, #guest-login .login-social-btn svg':
                    {
                        flexShrink: 0,
                    },
                '#login .gigya-input-checkbox, #register .gigya-input-checkbox, #gigya-login-popup-screen-container .gigya-input-checkbox, #guest-login .gigya-input-checkbox':
                    {
                        position: 'absolute',
                        opacity: 0,
                        width: '1px',
                        height: '1px',
                        margin: '-1px',
                        padding: 0,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        border: 0,
                    },
                '#login .gigya-input-checkbox + label.gigya-label, #register .gigya-input-checkbox + label.gigya-label, #gigya-login-popup-screen-container .gigya-input-checkbox + label.gigya-label, #guest-login .gigya-input-checkbox + label.gigya-label':
                    {
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        gap: remCalc(8),
                        minHeight: remCalc(16),
                    },
                '#register h2.gigya-composite-control-header.dln-completeForm__paragraph--secondary':
                    {
                        paddingLeft: 0,
                        color: DS.fgNeutralDefault,
                        fontWeight: 500,
                    },
                '#login .gigya-input-checkbox + label.gigya-label:before, #register .gigya-input-checkbox + label.gigya-label:before, #gigya-login-popup-screen-container .gigya-input-checkbox + label.gigya-label:before, #guest-login .gigya-input-checkbox + label.gigya-label:before':
                    {
                        content: '""',
                        position: 'static',
                        display: 'block',
                        boxSizing: 'border-box',
                        width: remCalc(16),
                        height: remCalc(16),
                        borderRadius: remCalc(4),
                        backgroundColor: DS.bgBaseDefault,
                        boxShadow: shadow.inputStrong,
                        border: `1px solid ${DS.borderNeutralHighlight}`,
                        flexShrink: 0,
                        zIndex: 1,
                    },
                '#login .gigya-input-checkbox:checked + label.gigya-label:before, #register .gigya-input-checkbox:checked + label.gigya-label:before, #gigya-login-popup-screen-container .gigya-input-checkbox:checked + label.gigya-label:before, #guest-login .gigya-input-checkbox:checked + label.gigya-label:before':
                    {
                        backgroundColor: DS.blue700,
                        boxShadow: shadow.elevationRaised,
                    },
                '#login .gigya-input-checkbox + label.gigya-label:after, #register .gigya-input-checkbox + label.gigya-label:after, #gigya-login-popup-screen-container .gigya-input-checkbox + label.gigya-label:after, #guest-login .gigya-input-checkbox + label.gigya-label:after':
                    {
                        content: '""',
                        position: 'absolute',
                        display: 'none',
                        top: remCalc(0),
                        left: remCalc(5),
                        width: remCalc(4),
                        height: remCalc(11),
                        border: `solid ${DS.fgNeutralInverse}`,
                        borderWidth: `0 2px 2px 0`,
                        transform: 'rotate(45deg)',
                        zIndex: 2,
                    },
                '#login .gigya-input-checkbox:checked + label.gigya-label:after, #register .gigya-input-checkbox:checked + label.gigya-label:after, #gigya-login-popup-screen-container .gigya-input-checkbox:checked + label.gigya-label:after, #guest-login .gigya-input-checkbox:checked + label.gigya-label:after':
                    {
                        display: 'block',
                    },
            },
        }
    },
}
